import {
  SystemCorrectionDetail,
  SystemCorrectionFieldMapEntry,
  SystemCorrectionFormObservedField,
  SystemCorrectionFormPatch,
  SystemCorrectionObservedFieldsResult,
  SystemCorrectionProjectCodes,
  SystemCorrectionRole,
  SystemCorrectionSectionSummary,
  SystemCorrectionStoredUserData,
  SystemCorrectionSubmitPayload,
} from '@/app/interfaces/system-correction';
import { systemReviewFieldKeys } from '@/app/interfaces/system-review';

interface NumberSelectOption {
  label: string;
  value: number;
}

interface TextSelectOption {
  label: string;
  value: string;
}

const systemTypeOptions: readonly NumberSelectOption[] = [
  { label: 'Sistema conversacional', value: 8 },
  { label: 'Sistema de análisis y extracción de información', value: 7 },
  { label: 'Sistema de recomendación y personalización', value: 6 },
  { label: 'Sistema predictivo y de apoyo de la decisión', value: 5 },
  { label: 'Sistema para la automatización inteligente de procesos', value: 4 },
  { label: 'Sistema de detección y supervisión', value: 3 },
  { label: 'Sistema a medida con funciones críticas o sensibles', value: 2 },
  { label: 'Otros', value: 1 },
];

const dataTypeOptions: readonly NumberSelectOption[] = [
  { label: 'Documentos', value: 1 },
  { label: 'Páginas', value: 2 },
  { label: 'Consultas', value: 3 },
  { label: 'Registros', value: 4 },
  { label: 'Imágenes', value: 5 },
  { label: 'Vídeo/Audio', value: 6 },
  { label: 'Datos estructurados', value: 7 },
  { label: 'Formularios', value: 8 },
  { label: 'Otros', value: 9 },
];

const administrationRoleOptions: readonly NumberSelectOption[] = [
  { label: 'Representante', value: 1 },
  { label: 'Fabricante', value: 2 },
  { label: 'Importador', value: 3 },
  { label: 'Distribuidor', value: 4 },
  { label: 'Desplegador', value: 5 },
  { label: 'Proveedor', value: 6 },
];

const explainabilityLevelOptions: readonly NumberSelectOption[] = [
  { label: 'Baja', value: 1 },
  { label: 'Medio', value: 2 },
  { label: 'Alto', value: 3 },
];

const systemProviderOptions: readonly TextSelectOption[] = [
  { label: 'Interno', value: 'Interno' },
  { label: 'Externo', value: 'Externo' },
  { label: 'Ambos', value: 'Ambos' },
];

const systemClassificationOptions: readonly TextSelectOption[] = [
  { label: 'Inaceptable', value: 'inaceptable' },
  { label: 'Alto', value: 'alto' },
  { label: 'Limitado', value: 'limitado' },
  { label: 'Mínimo', value: 'minimo' },
];

const systemTypeAliases: Readonly<Record<string, number>> = {
  otros: 1,
  criticosensible: 2,
  deteccionsupervision: 3,
  automatizacion: 4,
  predictivodecision: 5,
  recomendacionpersonalizacion: 6,
  analisisextraccion: 7,
  conversacional: 8,
};

const explainabilityLevelAliases: Readonly<Record<string, number>> = {
  media: 2,
};

const systemCorrectionSectionLabels: Readonly<Record<number, string>> = {
  1: 'Datos funcionales',
  2: 'Datos identificativos',
  3: 'Cuestiones técnicas',
  4: 'Datos de entrada',
  5: 'Cumplimiento de reglamento IA',
  6: 'Datos económicos',
  7: 'Gestión del riesgo',
};

const systemCorrectionFieldMapEntry = (
  formField: SystemCorrectionFormObservedField,
  section: number,
  fieldLabel: string
): SystemCorrectionFieldMapEntry => ({
  formField,
  section,
  sectionLabel: systemCorrectionSectionLabels[section],
  fieldLabel,
});

export const systemCorrectionFieldMap: readonly SystemCorrectionFieldMapEntry[] = [
  systemCorrectionFieldMapEntry('systemType', 1, 'Tipo de sistemas'),
  systemCorrectionFieldMapEntry('functionOfSystem', 1, 'Funciones o tareas a las que da soporte el sistema'),
  systemCorrectionFieldMapEntry('otherFunctions', 1, 'Otras funciones o tareas'),
  systemCorrectionFieldMapEntry('description', 1, 'Descripción del funcionamiento del sistema'),
  systemCorrectionFieldMapEntry('estimatedDeployDate', 1, 'Fecha estimada de despliegue del sistema'),

  systemCorrectionFieldMapEntry('aiSystemProviderType', 2, 'Proveedor del sistema de IA'),
  systemCorrectionFieldMapEntry('aiProviderName', 2, 'Nombre del proveedor'),

  systemCorrectionFieldMapEntry('aiModelName', 3, 'Modelo de IA'),

  systemCorrectionFieldMapEntry('estimatedFunctionalInputVolume', 4, 'Volumen funcional estimado de entrada'),

  systemCorrectionFieldMapEntry('administrationRole', 5, 'Papel de la administración respecto al sistema de IA'),
  systemCorrectionFieldMapEntry('dataType', 5, 'Tipo de datos'),
  systemCorrectionFieldMapEntry('otherDataType', 5, 'Otro tipo de datos'),
  systemCorrectionFieldMapEntry('explainabilityLevel', 5, 'Nivel de explicabilidad del modelo'),

  systemCorrectionFieldMapEntry('contractingImport', 6, 'Importe contratación (€)'),
  systemCorrectionFieldMapEntry('contractValidity', 6, 'Vigencia contratación (en meses)'),
  systemCorrectionFieldMapEntry('contractValidityDate', 6, 'Finalización vigencia contratación'),
  systemCorrectionFieldMapEntry('annualAmount', 6, 'Importe anual del sistema (€)'),
  systemCorrectionFieldMapEntry('estimatedAnnualSavings', 6, 'Ahorro anual estimado del sistema (€)'),

  systemCorrectionFieldMapEntry('systemClassification', 7, 'Clasificación del sistema'),
];

const systemCorrectionFieldToMapEntry: Readonly<
  Partial<Record<SystemCorrectionFormObservedField, SystemCorrectionFieldMapEntry>>
> = systemCorrectionFieldMap.reduce<Partial<Record<SystemCorrectionFormObservedField, SystemCorrectionFieldMapEntry>>>(
  (acc, entry) => {
    acc[entry.formField] = entry;
    return acc;
  },
  {}
);

const systemCorrectionFieldOrder: Readonly<Partial<Record<SystemCorrectionFormObservedField, number>>> =
  systemCorrectionFieldMap.reduce<Partial<Record<SystemCorrectionFormObservedField, number>>>((acc, entry, index) => {
    acc[entry.formField] = index;
    return acc;
  }, {});

export function emptySystemCorrectionProjectCodes(): SystemCorrectionProjectCodes {
  return {
    codigo_proyecto: '',
    codigo_proyecto_solicitud: '',
    proyecto: '',
    codigo_organismo: '',
  };
}

export function buildSystemCorrectionProjectCodes(
  detail: SystemCorrectionDetail,
  user: SystemCorrectionStoredUserData
): SystemCorrectionProjectCodes {
  if (detail.projectCodes) {
    return detail.projectCodes;
  }

  return {
    codigo_proyecto: '',
    codigo_proyecto_solicitud: '',
    proyecto: detail.projectAssociated ?? '',
    codigo_organismo: detail.codigo_organismo ?? detail.orgCodeCreator ?? user.orgCode ?? '',
  };
}

export function mapSystemCorrectionDetailToForm(
  detail: SystemCorrectionDetail,
  user: SystemCorrectionStoredUserData
): SystemCorrectionFormPatch {
  const projectCodes = buildSystemCorrectionProjectCodes(detail, user);

  return {
    projectCodes,
    systemName: detail.systemName ?? '',
    systemType: toSystemCorrectionSystemTypeValue(detail.systemType),
    otherSystemType: detail.otherSystemType ?? null,
    functionOfSystem: detail.functionOfSystem ?? [],
    otherFunctions: detail.otherFunctions ?? [],
    description: detail.description ?? '',
    estimatedDeployDate: toSystemCorrectionDateValue(detail.estimatedDeployDate),
    aiSystemProviderType: toSystemCorrectionTextSelectValue(detail.aiSystemProviderType, systemProviderOptions),
    aiProviderName: detail.aiProviderName ?? null,
    aiModelName: detail.aiModelName ?? null,
    estimatedFunctionalInputVolume: detail.estimatedFunctionalInputVolume ?? null,
    administrationRole: toSystemCorrectionAdministrationRoleValue(detail.administrationRole),
    dataType: toSystemCorrectionDataTypeValue(detail.dataType),
    otherDataType: detail.otherDataType ?? null,
    explainabilityLevel: toSystemCorrectionExplainabilityLevelValue(detail.explainabilityLevel),
    contractingImport: detail.contractingImport ?? null,
    contractValidity: detail.contractValidity ?? null,
    annualAmount: detail.annualAmount ?? null,
    estimatedAnnualSavings: detail.estimatedAnnualSavings ?? null,
    contractValidityDate: toSystemCorrectionDateValue(detail.contractValidityDate),
    systemClassification: toSystemCorrectionTextSelectValue(detail.systemClassification, systemClassificationOptions),
  };
}

export function normalizeSystemCorrectionFields(correctionFields: string[]): SystemCorrectionObservedFieldsResult {
  const uniqueCorrectionFields = [...new Set(correctionFields.filter((field) => field.trim() !== ''))];
  const knownCorrectionFields: SystemCorrectionFormObservedField[] = [];
  const unknownCorrectionFields: string[] = [];

  uniqueCorrectionFields.forEach((field) => {
    const formField = toSystemCorrectionFormField(field);

    if (formField) {
      knownCorrectionFields.push(formField);
      return;
    }

    unknownCorrectionFields.push(field);
  });

  return {
    correctionFields: uniqueCorrectionFields,
    knownCorrectionFields: [...new Set(knownCorrectionFields)],
    unknownCorrectionFields,
  };
}

export function mapSystemCorrectionFieldsToSummary(
  fields: readonly SystemCorrectionFormObservedField[]
): SystemCorrectionSectionSummary[] {
  const mappedEntries = fields
    .map((field) => systemCorrectionFieldToMapEntry[field] ?? null)
    .filter((entry): entry is SystemCorrectionFieldMapEntry => entry !== null);

  const uniqueEntries = [...new Map(mappedEntries.map((entry) => [entry.formField, entry])).values()];

  return uniqueEntries
    .sort((firstEntry, secondEntry) => {
      const sectionOrder = firstEntry.section - secondEntry.section;

      if (sectionOrder !== 0) {
        return sectionOrder;
      }

      return (
        (systemCorrectionFieldOrder[firstEntry.formField] ?? 0) -
        (systemCorrectionFieldOrder[secondEntry.formField] ?? 0)
      );
    })
    .reduce<SystemCorrectionSectionSummary[]>((summary, entry) => {
      const sectionSummary = summary.find((group) => group.section === entry.section);
      const field = {
        formField: entry.formField,
        label: entry.fieldLabel,
      };

      if (sectionSummary) {
        sectionSummary.fields.push(field);
        return summary;
      }

      summary.push({
        section: entry.section,
        sectionLabel: entry.sectionLabel,
        fields: [field],
      });

      return summary;
    }, []);
}

export function toSystemCorrectionStoredUserData(value: unknown): SystemCorrectionStoredUserData {
  if (!value || typeof value !== 'object') {
    return {};
  }

  const orgCode = Reflect.get(value, 'orgCode');
  const roleCode = Reflect.get(value, 'roleCode');
  const roles = Reflect.get(value, 'roles');

  return {
    orgCode: typeof orgCode === 'string' ? orgCode : undefined,
    roleCode: typeof roleCode === 'string' ? roleCode : undefined,
    roles: toSystemCorrectionRoles(roles),
  };
}

export function getSystemCorrectionSubmitRole(user: SystemCorrectionStoredUserData): SystemCorrectionRole | null {
  if (user.roles?.responsable_IA?.length) {
    return 'responsable_IA';
  }

  if (user.roles?.jefe_consumidor?.length) {
    return 'jefe_consumidor';
  }

  return null;
}

export function mapSystemCorrectionFormToPayload(
  formValue: SystemCorrectionFormPatch,
  systemId: string,
  role: SystemCorrectionRole
): SystemCorrectionSubmitPayload {
  return {
    systemId,
    systemName: formValue.systemName,
    systemType: Number(formValue.systemType),
    otherSystemType: Number(formValue.systemType) === 1 ? formValue.otherSystemType : null,
    functionOfSystem: toSystemCorrectionStringArray(formValue.functionOfSystem),
    otherFunctions: toSystemCorrectionStringArray(formValue.otherFunctions),
    description: formValue.description,
    estimatedDeployDate: formValue.estimatedDeployDate,
    estimatedFunctionalInputVolume: String(formValue.estimatedFunctionalInputVolume ?? ''),
    dataType: Number(formValue.dataType),
    otherDataType: Number(formValue.dataType) === 9 ? formValue.otherDataType : null,
    administrationRole: Number(formValue.administrationRole),
    explainabilityLevel: Number(formValue.explainabilityLevel),
    contractingImport: String(formValue.contractingImport ?? ''),
    annualAmount: String(formValue.annualAmount ?? ''),
    estimatedAnnualSavings: String(formValue.estimatedAnnualSavings ?? ''),
    lastModifiedDate: formValue.lastModifiedDate ?? null,
    contractValidity: String(formValue.contractValidity ?? ''),
    contractValidityDate: formValue.contractValidityDate,
    aiSystemProviderType: formValue.aiSystemProviderType ?? '',
    aiProviderName: formValue.aiProviderName ?? '',
    aiModelName: formValue.aiModelName ?? '',
    systemClassification: formValue.systemClassification ?? '',
    role,
  };
}

function toSystemCorrectionFormField(field: string): SystemCorrectionFormObservedField | null {
  if (!isSystemReviewField(field)) {
    return null;
  }

  return field;
}

function isSystemReviewField(field: string): field is SystemCorrectionFormObservedField {
  const reviewFields: readonly string[] = systemReviewFieldKeys;

  return reviewFields.includes(field);
}

function toSystemCorrectionNullableNumber(value: string | number | null | undefined): number | null {
  if (value == null || value === '') {
    return null;
  }

  const numericValue = Number(value);

  return Number.isNaN(numericValue) ? null : numericValue;
}

function toSystemCorrectionDateValue(value: string | number | null | undefined): Date | null {
  if (value == null || value === '') {
    return null;
  }

  const dateValue = String(value);
  const compactDateMatch = /^(\d{4})(\d{2})(\d{2})$/.exec(dateValue);

  if (compactDateMatch) {
    return new Date(Number(compactDateMatch[1]), Number(compactDateMatch[2]) - 1, Number(compactDateMatch[3]));
  }

  const slashDateMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dateValue);

  if (slashDateMatch) {
    return new Date(Number(slashDateMatch[3]), Number(slashDateMatch[2]) - 1, Number(slashDateMatch[1]));
  }

  return null;
}

function toSystemCorrectionSystemTypeValue(value: string | number | null | undefined): number | null {
  return (
    toSystemCorrectionNumberSelectValue(value, systemTypeOptions) ??
    toSystemCorrectionAliasSelectValue(value, systemTypeAliases) ??
    toSystemCorrectionNullableNumber(value)
  );
}

function toSystemCorrectionDataTypeValue(value: string | number | null | undefined): number | null {
  return toSystemCorrectionNumberSelectValue(value, dataTypeOptions) ?? toSystemCorrectionNullableNumber(value);
}

function toSystemCorrectionAdministrationRoleValue(value: string | number | null | undefined): number | null {
  return (
    toSystemCorrectionNumberSelectValue(value, administrationRoleOptions) ?? toSystemCorrectionNullableNumber(value)
  );
}

function toSystemCorrectionExplainabilityLevelValue(value: string | number | null | undefined): number | null {
  return (
    toSystemCorrectionNumberSelectValue(value, explainabilityLevelOptions) ??
    toSystemCorrectionAliasSelectValue(value, explainabilityLevelAliases) ??
    toSystemCorrectionNullableNumber(value)
  );
}

function toSystemCorrectionNumberSelectValue(
  value: string | number | null | undefined,
  options: readonly NumberSelectOption[]
): number | null {
  if (typeof value === 'number') {
    return options.some((option) => option.value === value) ? value : null;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const normalizedValue = normalizeSystemCorrectionSelectText(value);
  const option = options.find((item) => {
    return (
      normalizeSystemCorrectionSelectText(item.label) === normalizedValue ||
      normalizeSystemCorrectionSelectText(String(item.value)) === normalizedValue
    );
  });

  return option?.value ?? null;
}

function toSystemCorrectionTextSelectValue(
  value: string | null | undefined,
  options: readonly TextSelectOption[]
): string | null {
  if (value == null || value === '') {
    return null;
  }

  const normalizedValue = normalizeSystemCorrectionSelectText(value);
  const option = options.find((item) => {
    return (
      normalizeSystemCorrectionSelectText(item.label) === normalizedValue ||
      normalizeSystemCorrectionSelectText(item.value) === normalizedValue
    );
  });

  return option?.value ?? null;
}

function toSystemCorrectionAliasSelectValue(
  value: string | number | null | undefined,
  aliases: Readonly<Record<string, number>>
): number | null {
  if (typeof value !== 'string') {
    return null;
  }

  return aliases[normalizeSystemCorrectionSelectText(value)] ?? null;
}

function normalizeSystemCorrectionSelectText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

function toSystemCorrectionRoles(value: unknown): Partial<Record<SystemCorrectionRole, string[]>> | undefined {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  return {
    responsable_IA: toStringArray(Reflect.get(value, 'responsable_IA')),
    jefe_consumidor: toStringArray(Reflect.get(value, 'jefe_consumidor')),
  };
}

function toStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  return value.filter((item): item is string => typeof item === 'string');
}

function toSystemCorrectionStringArray(value: string[]): string[] {
  return value.filter((item): item is string => typeof item === 'string');
}
